import { FC, useEffect, useState, KeyboardEvent, useMemo } from 'react';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useMutation, useQuery } from 'react-query';
import {
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Paper,
  OutlinedInput,
  Container,
} from '@material-ui/core';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { ReactComponent as LogoIcon } from 'assets/logo.svg';
import { useHistory, useLocation } from 'react-router';
import { handleCompanyUrl } from 'utils/organizationUtils';
import EndUserHeader from 'components/Headers/EndUserHeader';
import EndUserFooter from 'components/Headers/EndUserFooter';
import { getTenantFromSubdomain, isValidTenant } from 'utils/tenantUtils';
import Easy from '../../assets/easyidentity.jpg';

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/;

const SignIn: FC = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<number>(0);
  const isValidEmail = useMemo(() => emailRegex.test(email), [email]);
  const [loginUrl, setLoginUrl] = useState<string | null>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const domain = useMemo(() => {
    const tenant = getTenantFromSubdomain();

    if (tenant !== null && isValidTenant(tenant)) {
      return tenant;
    }

    // Fallback: read domain from URL param (e.g. app.easyidentity.co/login?domain=testclient02)
    const urlDomain = new URLSearchParams(window.location.search).get('domain');
    if (urlDomain) return urlDomain;

    return '';
  }, []);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const {
    userStore: { login },
    endUserSettingsFormStore: { getPortalSettings, endUserConfigSettings },
    notificationStore: { openNotification },
  } = useStore();

  // Check if OAuth params are present in URL
  const hasOAuthParams = useMemo(() => {
    return params.has('response_type') && params.has('client_id') && params.has('redirect_uri');
  }, [params]);

  const portalSettingsgetReq = useQuery('portalSettingsgetReq', () => getPortalSettings(), {
    onError: () => openNotification('error', `Can't get data`),
  });

  useEffect(() => {
    if (endUserConfigSettings?.redirectLoginUrl) {
      window.location.href = endUserConfigSettings.redirectLoginUrl;
    }
  }, [endUserConfigSettings?.redirectLoginUrl]);

  useEffect(() => {
    document.title = 'EasyIdentity: User Account Registration';
  }, []);

  const bodyStyle = useMemo(
    () => ({
      backgroundImage: `url(${endUserConfigSettings?.bannerImage || Easy})`,
      backgroundSize: 'cover',
      backgroundAttachment: 'fixed',
    }),
    [endUserConfigSettings?.bannerImage]
  );

  const loginRequest = useMutation(
    () => {
      if (hasOAuthParams) {
        // OAuth login API call
        const oauthData = {
          email,
          password,
          response_type: params.get('response_type') || 'code',
          client_id: params.get('client_id') || '',
          redirect_uri: params.get('redirect_uri') || '',
          domain,
          state: 'random_state_string',
        };

        return fetch(`${process.env.REACT_APP_API_BASE_URL}oauth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(oauthData),
        }).then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        });
      } else {
        // Normal login API call
        return login({ email, password, domain });
      }
    },
    {
      mutationKey: 'loginRequest',
      onSuccess: (data) => {
        if (hasOAuthParams) {
          if (data.callback_url) {
            const callbackUrl = data.callback_url;
            window.location.href = callbackUrl;
            return;
          }
        }
        // Continue with existing normal login logic
        const responseData = hasOAuthParams ? data : data.data.data;
        console.log('responseData ', responseData);
        console.log('responseData?.user ', responseData?.user);
        localStorage.setItem('Profile', JSON.stringify(responseData.user));
        localStorage.setItem('token', responseData.token);
        localStorage.setItem('organizationName', responseData.organizationName);
        if (rememberMe) {
          Cookies.set('email', responseData.user.email, { expires: 7, secure: true, sameSite: 'strict' });
        }

        if (
          responseData?.user?.isTwoFactorEnable === true &&
          (responseData.user.permission === 'EndUser' ||
            responseData.user.role === 'end_user' ||
            responseData.user.permission === 'Manager' ||
            responseData.user.role === 'manager')
        ) {
          const fqdn = responseData?.organizationName;
          const token = responseData?.token;
          localStorage.setItem('token', token);
          setLoader(true);
          const fetchData = async () => {
            try {
              const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}auth/2fa/enable`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: ` ${token}`,
                  Accept: 'application/json',
                  fqdn: fqdn,
                },
              });
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              } else {
                const responseData = await response.json();
                const qrCodeUrl = responseData.qrCodeUrl;
                const secretKey = responseData.secretKey;
                if (qrCodeUrl && secretKey) {
                  localStorage.setItem('QRCode', qrCodeUrl);
                  localStorage.setItem('secretKey', secretKey);
                  setLoader(false);
                  history.push(handleCompanyUrl('/2-fa'));
                } else {
                  setLoader(false);
                  history.push(handleCompanyUrl('/authenticate'));
                }
              }
            } catch (error) {
              setLoader(false);
              console.error('Error fetching data:', error);
              openNotification('error', 'An unexpected error occurred. Please try again.');
            }
          };
          fetchData();
        } else if (
          (responseData?.user?.isTwoFactorEnable === false &&
            (responseData.user.permission === 'EndUser' || responseData.user.role === 'end_user')) ||
          responseData.user.permission === 'Manager' ||
          responseData.user.role === 'manager'
        ) {
          const hostName = responseData.user.hostName;
          const protocol = window.location.protocol;
          const hostname = window.location.hostname;
          const port = window.location.port;
          const token =
            responseData.token?.replace('Bearer ', '') || localStorage.getItem('token')?.replace('Bearer ', '');
          const userData = JSON.stringify(responseData.user);

          const orgName = responseData.organizationName;
          if (hostName && !hostname.startsWith(`${hostName}.`)) {
            const isLocalhost = hostname.includes('localhost');
            let baseHost;
            if (isLocalhost) {
              baseHost = `localhost${port ? ':' + port : ''}`;
            } else {
              const parts = hostname.split('.');
              baseHost = parts.length === 2 ? hostname : parts.slice(1).join('.');
            }
            window.location.href = `${protocol}//${hostName}.${baseHost}/admin/dashboard?token=${token}&userData=${encodeURIComponent(
              userData
            )}&orgName=${encodeURIComponent(orgName || '')}`;
          } else {
            history.push('/admin/dashboard');
          }
        } else if (responseData?.user?.isTwoFactorEnable === false) {
          const hostName = responseData.user.hostName || domain;
          console.log('domain (from url/subdomain):', domain);
          console.log('hostName (from responseData):', responseData.user.hostName);
          console.log('final hostName used:', hostName);
          console.log('current hostname:', window.location.hostname);
          const protocol = window.location.protocol;
          const hostname = window.location.hostname;
          const port = window.location.port;
          const token =
            responseData.token?.replace('Bearer ', '') || localStorage.getItem('token')?.replace('Bearer ', '');
          const userData = JSON.stringify(responseData.user);
          const orgName = responseData.organizationName;
          if (hostName && !hostname.startsWith(`${hostName}.`)) {
            const isLocalhost = hostname.includes('localhost');
            let baseHost;
            if (isLocalhost) {
              baseHost = `localhost${port ? ':' + port : ''}`;
            } else {
              const parts = hostname.split('.');
              baseHost = parts.length === 2 ? hostname : parts.slice(1).join('.');
            }
            window.location.href = `${protocol}//${hostName}.${baseHost}/dashboard?token=${token}&userData=${encodeURIComponent(
              userData
            )}&orgName=${encodeURIComponent(orgName || '')}`;
          } else {
            history.push('/dashboard');
          }
        } else {
          const fqdn = responseData?.organizationName;
          const token = responseData?.token;
          localStorage.setItem('token', token);
          setLoader(true);
          const fetchData = async () => {
            try {
              const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}auth/2fa/enable`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: ` ${token}`,
                  Accept: 'application/json',
                  fqdn: fqdn,
                },
              });
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              } else {
                const responseData = await response.json();
                const qrCodeUrl = responseData.qrCodeUrl;
                const secretKey = responseData.secretKey;
                if (qrCodeUrl && secretKey) {
                  localStorage.setItem('QRCode', qrCodeUrl);
                  localStorage.setItem('secretKey', secretKey);
                  setLoader(false);
                  history.push(handleCompanyUrl('/2-fa'));
                } else {
                  setLoader(false);
                  history.push(handleCompanyUrl('/authenticate'));
                }
              }
            } catch (error) {
              setLoader(false);
              console.error('Error fetching data:', error);
              openNotification('error', 'An unexpected error occurred. Please try again.');
            }
          };
          fetchData();
        }
      },
      onError: (error: any) => {
        let errorMessage;
        if (hasOAuthParams) {
          // Handle OAuth login error
          errorMessage = error?.message || 'OAuth login failed';
        } else {
          // Handle normal login error
          errorMessage = error?.response?.data?.message || error?.message || 'Something went wrong';
        }
        openNotification('error', errorMessage);
      },
    }
  );
  useEffect(() => {
    // const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userData = urlParams.get('userData');
    const orgName = urlParams.get('orgName');
    if (token && userData) {
      try {
        const user = JSON.parse(decodeURIComponent(userData));
        localStorage.setItem('token', `Bearer ${token}`);
        localStorage.setItem('Profile', JSON.stringify(user));
        if (orgName) {
          localStorage.setItem('organizationName', decodeURIComponent(orgName));
        }
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => {
          if (user.permission === 'EndUser' || user.role === 'end_user') {
            window.location.href = '/admin/dashboard';
          } else {
            window.location.href = '/dashboard';
          }
        }, 100);
        return;
      } catch (error) {
        console.error('Error parsing URL parameters:', error);
      }
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('Profile');
      localStorage.removeItem('organizationName');
    }
  }, []);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        const currentSubdomain = parts.length > 1 && hostname !== 'localhost' ? parts[0] : '';

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}auth/social/google`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            fqdn: currentSubdomain || 'googleAuth',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const contentType = response.headers.get('Content-Type');
        if (contentType && contentType.includes('application/json')) {
          const responseData = await response.json();
          setLoginUrl(responseData);
          setLoading(false);
        } else {
          const responseData = await response.text();
          setLoginUrl(responseData);
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    document.title = 'EasyIdentity: Admin Login';
    const emailData = Cookies.get('email');
    if (emailData) {
      setEmail(emailData);
    }
  }, []);

  const handlekeydown = (event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement | HTMLDivElement>) => {
    if (event.code === 'Space') {
      event.preventDefault();
    }
  };
  const handleEmail = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setEmail(event.target.value);
  };
  // const handleDomain = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  //   setDomain(event.target.value);
  // };
  const handleSetPassword = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    setPassword(target?.value);
  };

  useAppPreloader(loginRequest.isLoading || loading || portalSettingsgetReq.isLoading || loader);

  return (
    <div style={domain ? bodyStyle : undefined}>
      {domain ? (
        <div>
          <EndUserHeader />
        </div>
      ) : (
        <div className={classes.topBar}>
          <LogoIcon style={{ marginLeft: '40px', width: '140px', height: '52px' }} />
        </div>
      )}

      <Container maxWidth={false} className={classes.container}>
        <Grid container className={classes.main} justify='center' alignItems='center'>
          <Grid item xl={3} lg={4} md={5} sm={7} xs={11}>
            <Paper className={classes.paper} elevation={3}>
              <h1 className={classes.title}>Login</h1>
              <Grid container justify='center'>
                <TextField
                  className={classes.formControl}
                  color='primary'
                  id='email'
                  value={email}
                  onKeyDown={(event) => handlekeydown(event)}
                  onChange={(event) => handleEmail(event)}
                  required
                  fullWidth
                  variant='outlined'
                  label='Email'
                  autoFocus
                />
                {!isValidEmail && email.trim() !== '' && (
                  <span className={classes.error}>Enter a valid email address to use as your registered email ID</span>
                )}
                <FormControl className={classes.formControl} required>
                  <InputLabel variant='outlined' htmlFor='password'>
                    Password
                  </InputLabel>
                  <OutlinedInput
                    id='password'
                    label='password'
                    fullWidth
                    required
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onKeyDown={(event) => handlekeydown(event)}
                    onChange={(event) => handleSetPassword(event)}
                    endAdornment={
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
              <Grid className={classes.formControl} container justify='space-between'>
                <Grid className={classes.forgotPassword}>
                  <Link className={classes.link} to={'/admin/forgot-password'}>
                    FORGOT PASSWORD?
                  </Link>
                </Grid>
                <Grid className={classes.forgotPassword}>
                  <Link className={classes.link} to={'/register'}>
                    REGISTER
                  </Link>
                </Grid>
              </Grid>
              <FormControlLabel
                className={classes.formControl}
                control={
                  <Checkbox
                    onChange={(event) => {
                      setRememberMe(event.target.checked ? 1 : 0);
                    }}
                  />
                }
                label='Remember me'
              />
              <Grid className={classes.formControl} container justify='space-between'>
                <Button
                  className={classes.button}
                  variant='contained'
                  color='primary'
                  onClick={() => {
                    loginRequest.mutate();
                  }}
                  disabled={!isValidEmail || !password}
                >
                  LOG IN
                </Button>
                {loginUrl && loginUrl !== '' && (
                  <>
                    <span className={classes.titles}>OR</span>
                    <Button className={classes.loginWithGoogleBtn}>
                      <a href={loginUrl}>Sign in with Google</a>
                    </Button>
                  </>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {domain && <EndUserFooter />}
    </div>
  );
});

export default SignIn;
